
import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Category, TransactionType, Household, GroupGuest } from '../types';
import { scanReceipt } from '../services/aiService';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, User, Trash2, Camera, Sparkles, UserCircle, Crop, X } from 'lucide-react';
import { format } from 'date-fns';

const TransactionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [scanning, setScanning] = useState(false);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [guests, setGuests] = useState<GroupGuest[]>([]);
  const [saveReceipts, setSaveReceipts] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [scanSourceImage, setScanSourceImage] = useState<string | null>(null);
  const [pendingScanFile, setPendingScanFile] = useState<File | null>(null);
  const [cropBox, setCropBox] = useState({ x: 8, y: 8, width: 84, height: 84 });
  const [showMobileScanOptions, setShowMobileScanOptions] = useState(false);

  const [formData, setFormData] = useState({
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'expense' as TransactionType,
    category: Category.FOOD,
    note: '',
    household_id: '',
    payer_guest_id: '', // Empty means Current User paid
    receipt_url: '',
    is_settled: false,
    owner_id: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const scanPreviewUrlRef = useRef<string | null>(null);
  const isMobileDevice = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

  useEffect(() => {
    fetchUserData();
    if (isEdit) fetchTransaction();
  }, [id]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      sessionStorage.removeItem('camera_capture_in_progress');
    }, 15000);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (formData.household_id) {
      fetchGuests(formData.household_id);
    } else {
      setGuests([]);
      setFormData(prev => ({ ...prev, payer_guest_id: '' }));
    }
  }, [formData.household_id]);

  const fetchUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setCurrentUserId(user.id);

    const { data: profile } = await supabase.from('user_profiles').select('save_receipts').eq('id', user.id).single();
    if (profile) setSaveReceipts(profile.save_receipts);

    const { data: memberData } = await supabase.from('household_members').select('household_id').eq('user_id', user.id);
    if (memberData && memberData.length > 0) {
      const ids = memberData.map(m => m.household_id);
      const { data: householdData } = await supabase.from('households').select('*').in('id', ids);
      if (householdData) setHouseholds(householdData);
    }
  };

  const fetchGuests = async (householdId: string) => {
    const { data } = await supabase.from('group_guests').select('*').eq('household_id', householdId);
    setGuests(data || []);
  };

  const fetchTransaction = async () => {
    setFetching(true);
    const { data, error } = await supabase.from('transactions').select('*').eq('id', id).single();
    if (error) {
      navigate('/history');
    } else if (data) {
      setFormData({
        amount: data.amount.toString(),
        date: data.date,
        type: data.type,
        category: data.category as Category,
        note: data.note || '',
        household_id: data.household_id || '',
        payer_guest_id: data.payer_guest_id || '',
        receipt_url: data.receipt_url || '',
        is_settled: data.is_settled || false,
        owner_id: data.user_id
      });
    }
    setFetching(false);
  };

  const readFileAsDataUrl = async (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Unable to read image.'));
      reader.readAsDataURL(file);
    });
  };

  const clampPercent = (value: number, min: number, max: number) => {
    return Math.min(max, Math.max(min, value));
  };

  const updateCropBox = (key: 'x' | 'y' | 'width' | 'height', value: number) => {
    setCropBox(prev => {
      const next = { ...prev, [key]: value };
      next.width = clampPercent(next.width, 20, 100);
      next.height = clampPercent(next.height, 20, 100);
      next.x = clampPercent(next.x, 0, 100 - next.width);
      next.y = clampPercent(next.y, 0, 100 - next.height);
      return next;
    });
  };

  const cropImageFile = async (source: string, fileName: string) => {
    return new Promise<File>((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        const cropX = Math.round((cropBox.x / 100) * img.width);
        const cropY = Math.round((cropBox.y / 100) * img.height);
        const cropWidth = Math.round((cropBox.width / 100) * img.width);
        const cropHeight = Math.round((cropBox.height / 100) * img.height);

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, cropWidth);
        canvas.height = Math.max(1, cropHeight);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Unable to crop image.'));
          return;
        }

        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Unable to crop image.'));
            return;
          }
          resolve(new File([blob], fileName, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.95);
      };
      img.onerror = () => reject(new Error('Unable to load image for crop.'));
      img.src = source;
    });
  };

  const generateScanVariants = async (file: File) => {
    return new Promise<string[]>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const buildVariant = (maxWidth: number, quality: number, enhance = false) => {
            const canvas = document.createElement('canvas');
            const scale = Math.min(1, maxWidth / img.width);
            const width = Math.max(1, Math.round(img.width * scale));
            const height = Math.max(1, Math.round(img.height * scale));
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) return '';

            ctx.drawImage(img, 0, 0, width, height);

            if (enhance) {
              const imageData = ctx.getImageData(0, 0, width, height);
              const data = imageData.data;
              for (let i = 0; i < data.length; i += 4) {
                const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                const boosted = gray > 160 ? 255 : gray < 90 ? 0 : Math.min(255, Math.max(0, (gray - 90) * 1.8));
                data[i] = boosted;
                data[i + 1] = boosted;
                data[i + 2] = boosted;
              }
              ctx.putImageData(imageData, 0, 0);
            }

            return canvas.toDataURL('image/jpeg', quality);
          };

          const variants = [
            buildVariant(1800, 0.95),
            buildVariant(1400, 0.92, true),
            buildVariant(1200, 0.9)
          ].filter(Boolean);

          resolve(variants);
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const processScannedFile = async (file: File) => {
    setScanning(true);
    try {
      const originalBase64 = await readFileAsDataUrl(file);
      const generatedVariants = await generateScanVariants(file);
      const scanInputs = [originalBase64, ...generatedVariants].filter(Boolean);
      const storageImage = generatedVariants[0] || originalBase64;
      let uploadedUrl = '';
      const { data: { user } } = await supabase.auth.getUser();

      if (saveReceipts && user) {
        const fileName = `${user.id}/${Date.now()}.jpg`;
        const blob = await (await fetch(storageImage)).blob();
        await supabase.storage.from('receipts').upload(fileName, blob);
        const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(fileName);
        uploadedUrl = publicUrl;
      }

      const result = await scanReceipt(scanInputs);

      if (result) {
        setFormData(prev => ({
          ...prev,
          amount: result.amount > 0 ? result.amount.toString() : prev.amount,
          category: result.category || prev.category,
          date: result.date || prev.date,
          note: result.note || prev.note,
          receipt_url: uploadedUrl || prev.receipt_url
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  const openPreviewForCrop = (file: File) => {
    if (scanPreviewUrlRef.current) {
      URL.revokeObjectURL(scanPreviewUrlRef.current);
    }

    const preview = URL.createObjectURL(file);
    scanPreviewUrlRef.current = preview;
    setPendingScanFile(file);
    setScanSourceImage(preview);
    setCropBox({ x: 8, y: 8, width: 84, height: 84 });
  };

  const handleUploadSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processScannedFile(file);
    e.target.value = '';
  };

  const handleCameraSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    sessionStorage.removeItem('camera_capture_in_progress');
    if (!file) return;
    openPreviewForCrop(file);
    e.target.value = '';
  };

  const openMobileCamera = () => {
    sessionStorage.setItem('camera_capture_in_progress', '1');
    cameraInputRef.current?.click();
  };

  const openDevicePicker = () => {
    fileInputRef.current?.click();
  };

  const handleScanButtonClick = () => {
    if (isMobileDevice) {
      setShowMobileScanOptions(true);
      return;
    }
    openDevicePicker();
  };

  const closeCropModal = () => {
    if (scanPreviewUrlRef.current) {
      URL.revokeObjectURL(scanPreviewUrlRef.current);
      scanPreviewUrlRef.current = null;
    }
    sessionStorage.removeItem('camera_capture_in_progress');
    setScanSourceImage(null);
    setPendingScanFile(null);
  };

  const handleUseFullImage = async () => {
    if (!pendingScanFile) return;
    const file = pendingScanFile;
    closeCropModal();
    await processScannedFile(file);
  };

  const handleCropAndScan = async () => {
    if (!scanSourceImage || !pendingScanFile) return;
    const croppedFile = await cropImageFile(scanSourceImage, pendingScanFile.name || 'bill-scan.jpg');
    closeCropModal();
    await processScannedFile(croppedFile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.amount) return;
    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const payload = {
      user_id: user.id,
      amount: parseFloat(formData.amount),
      date: formData.date,
      type: formData.type,
      category: formData.category,
      note: formData.note,
      household_id: formData.household_id || null,
      payer_guest_id: formData.payer_guest_id || null,
      receipt_url: formData.receipt_url || null,
      is_settled: formData.is_settled
    };

    const { error } = isEdit
      ? await supabase.from('transactions').update(payload).eq('id', id)
      : await supabase.from('transactions').insert([payload]);

    if (error) alert(error.message);
    else navigate(-1);
    setLoading(false);
  };

  const isGroupEntry = !!formData.household_id;

  return (
    <div className="max-w-lg mx-auto pb-8">
      {showMobileScanOptions && (
        <div className="fixed inset-0 z-[115] bg-slate-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-black text-slate-900">Scan Bill</h2>
                <p className="text-xs text-slate-500 font-bold">Choose how you want to add the bill photo</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileScanOptions(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <button
                type="button"
                onClick={() => {
                  setShowMobileScanOptions(false);
                  openMobileCamera();
                }}
                className="w-full py-4 rounded-[1.4rem] bg-[#0f172a] text-amber-400 font-black text-sm shadow-xl flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Camera size={16} /> Open Camera
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowMobileScanOptions(false);
                  openDevicePicker();
                }}
                className="w-full py-4 rounded-[1.4rem] border border-slate-200 bg-white text-slate-700 font-black text-sm transition-all hover:bg-slate-50"
              >
                Upload From Device
              </button>
            </div>
          </div>
        </div>
      )}

      {scanSourceImage && (
        <div className="fixed inset-0 z-[120] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-[2rem] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-black text-slate-900">Crop Bill Photo</h2>
                <p className="text-xs text-slate-500 font-bold">Adjust the area before scanning</p>
              </div>
              <button
                type="button"
                onClick={closeCropModal}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 md:p-6 space-y-5">
              <div className="relative bg-slate-100 rounded-[1.5rem] overflow-hidden border border-slate-200">
                <img src={scanSourceImage} alt="Bill preview" className="w-full max-h-[55vh] object-contain" />
                <div
                  className="absolute border-[3px] border-amber-400 rounded-[1rem] shadow-[0_0_0_9999px_rgba(15,23,42,0.45)] pointer-events-none"
                  style={{
                    left: `${cropBox.x}%`,
                    top: `${cropBox.y}%`,
                    width: `${cropBox.width}%`,
                    height: `${cropBox.height}%`
                  }}
                >
                  <div className="absolute inset-x-3 top-3 border-t-2 border-dashed border-amber-200/80"></div>
                  <div className="absolute inset-x-3 bottom-3 border-t-2 border-dashed border-amber-200/80"></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-xs font-black text-slate-600">
                  Left
                  <input type="range" min="0" max={100 - cropBox.width} value={cropBox.x} onChange={(e) => updateCropBox('x', Number(e.target.value))} className="w-full mt-2" />
                </label>
                <label className="block text-xs font-black text-slate-600">
                  Top
                  <input type="range" min="0" max={100 - cropBox.height} value={cropBox.y} onChange={(e) => updateCropBox('y', Number(e.target.value))} className="w-full mt-2" />
                </label>
                <label className="block text-xs font-black text-slate-600">
                  Width
                  <input type="range" min="20" max={100 - cropBox.x} value={cropBox.width} onChange={(e) => updateCropBox('width', Number(e.target.value))} className="w-full mt-2" />
                </label>
                <label className="block text-xs font-black text-slate-600">
                  Height
                  <input type="range" min="20" max={100 - cropBox.y} value={cropBox.height} onChange={(e) => updateCropBox('height', Number(e.target.value))} className="w-full mt-2" />
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleUseFullImage}
                  className="flex-1 py-4 rounded-[1.4rem] border border-slate-200 bg-white text-slate-700 font-black text-sm transition-all hover:bg-slate-50"
                >
                  Use Full Image
                </button>
                <button
                  type="button"
                  onClick={handleCropAndScan}
                  className="flex-1 py-4 rounded-[1.4rem] bg-[#0f172a] text-amber-400 font-black text-sm shadow-xl flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <Crop size={16} /> Crop And Scan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-500 hover:text-gray-900"><ChevronLeft className="w-5 h-5 mr-1" /> Back</button>
        {isEdit && formData.owner_id === currentUserId && (
          <button onClick={async () => {
            if (window.confirm("Delete this record permanently?")) {
              await supabase.from('transactions').delete().eq('id', id);
              navigate(-1);
            }
          }} className="text-red-500 p-2 hover:bg-red-50 rounded-xl transition-all"><Trash2 size={20} /></button>
        )}
      </div>

      <div className="flex items-center justify-between mb-8 px-2">
        <h1 className="text-2xl font-black text-[#0f172a]">{isEdit ? 'Edit Entry' : 'Add Entry'}</h1>
        {!isEdit && (
          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={handleScanButtonClick}
              className="flex items-center bg-amber-50 text-amber-600 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-amber-200 active:scale-95 shadow-sm"
            >
              <Camera size={14} className="mr-2" /> Scan Bill
            </button>
            <label className="flex items-center cursor-pointer gap-2 mr-1">
              <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider">Cloud Backup</span>
              <div className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${saveReceipts ? 'bg-amber-400' : 'bg-slate-200'}`}>
                <input type="checkbox" className="sr-only" checked={saveReceipts} onChange={(e) => setSaveReceipts(e.target.checked)} />
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${saveReceipts ? 'translate-x-[14px]' : 'translate-x-[2px]'}`} />
              </div>
            </label>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleUploadSelect}
        />
        <input
          type="file"
          ref={cameraInputRef}
          className="hidden"
          accept="image/*"
          capture="environment"
          onChange={handleCameraSelect}
        />
      </div>

      {scanning && (
        <div className="bg-[#0f172a] p-8 rounded-[2.5rem] mb-8 text-amber-400 shadow-2xl animate-pulse flex flex-col items-center">
          <Sparkles className="w-10 h-10 mb-4 animate-spin" />
          <p className="font-black text-xs uppercase tracking-[0.2em]">Digitizing Bill...</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Choose Group</label>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button type="button" onClick={() => setFormData({ ...formData, household_id: '' })} className={`px-6 py-4 rounded-[1.8rem] border text-xs font-black uppercase tracking-widest transition-all shrink-0 ${formData.household_id === '' ? 'bg-[#0f172a] text-white shadow-lg' : 'bg-white text-slate-400'}`}>Personal</button>
            {households.map(hh => (
              <button key={hh.id} type="button" onClick={() => setFormData({ ...formData, household_id: hh.id })} className={`px-6 py-4 rounded-[1.8rem] border text-xs font-black uppercase tracking-widest transition-all shrink-0 ${formData.household_id === hh.id ? 'bg-amber-500 text-white shadow-lg' : 'bg-white text-slate-400'}`}>{hh.name}</button>
            ))}
          </div>
        </div>

        {isGroupEntry && (
          <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100 space-y-3">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Paid By</label>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payer_guest_id: '' })}
                className={`px-5 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-2 ${formData.payer_guest_id === '' ? 'bg-[#0f172a] text-[#fbbf24]' : 'bg-white text-slate-400'}`}
              >
                <User size={14} /> Me
              </button>
              {guests.map(guest => (
                <button
                  key={guest.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, payer_guest_id: guest.id })}
                  className={`px-5 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-2 ${formData.payer_guest_id === guest.id ? 'bg-amber-500 text-white' : 'bg-white text-slate-400'}`}
                >
                  <UserCircle size={14} /> {guest.name}
                </button>
              ))}
            </div>
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider px-1">
              {formData.payer_guest_id ? 'Recording this as a payment made by a roommate.' : 'Recording this as a payment you made for the group.'}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Amount (INR)</label>
            <input type="number" required placeholder="0.00" className="w-full px-8 py-5 bg-white border border-slate-100 rounded-[2rem] text-3xl font-black text-[#0f172a] focus:ring-4 focus:ring-slate-50 outline-none transition-all" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
          </div>
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Date</label>
            <input type="date" required className="w-full px-8 py-5 bg-white border border-slate-100 rounded-[2rem] font-bold text-slate-600 focus:ring-4 focus:ring-slate-50 outline-none" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Type</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { id: 'expense', label: 'Expense' },
              { id: 'income', label: 'Income' },
              { id: 'debt', label: 'Borrowed' },
              { id: 'receivable', label: 'Lent' }
            ].map(type => (
              <button key={type.id} type="button" onClick={() => setFormData({ ...formData, type: type.id as any })} className={`py-4 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${formData.type === type.id ? 'bg-[#0f172a] text-white shadow-lg' : 'bg-white text-slate-400'}`}>{type.label}</button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Category</label>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {Object.values(Category).map(cat => (
              <button key={cat} type="button" onClick={() => setFormData({ ...formData, category: cat })} className={`px-6 py-4 rounded-[1.8rem] border text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${formData.category === cat ? 'bg-amber-400 text-[#0f172a] border-amber-400 shadow-md' : 'bg-white text-slate-400'}`}>{cat}</button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Note (Optional)</label>
          <textarea placeholder="Description of the record..." className="w-full px-8 py-5 bg-white border border-slate-100 rounded-[2rem] font-medium text-slate-600 focus:ring-4 focus:ring-slate-50 outline-none min-h-[120px]" value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} />
        </div>

        <button type="submit" disabled={loading} className="w-full bg-[#0f172a] text-[#fbbf24] py-6 rounded-[2.5rem] font-black shadow-2xl flex items-center justify-center hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 mt-10 border border-white/5">
          {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Save className="mr-3 w-5 h-5" /> {isEdit ? 'Update Entry' : 'Save Entry'}</>}
        </button>
      </form>
    </div>
  );
};

export default TransactionForm;
